import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, ArrowRightLeft, Save, Sparkles, Loader2, FileDown, Stamp } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { RagBadge, RAG_OPTIONS } from "@/components/Rag";
import { Select } from "@/pages/Projects";
import { LogSection, StatusPill } from "@/components/LogSection";
import { Dependencies } from "@/components/Dependencies";
import { Documents } from "@/components/Documents";
import { ImportControls } from "@/components/ImportControls";
import { Schedule } from "@/components/Schedule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useBranding } from "@/context/BrandingContext";
import { useEntitlements } from "@/context/EntitlementsContext";
import { toast } from "sonner";

const TABS = ["Overview", "Plan", "RAID", "Milestones", "Dependencies", "Decisions", "Changes", "Lessons", "Benefits", "Status Updates", "Documents"];

// Tabs that only exist on plans including the relevant feature.
const TAB_FEATURES = {
  Dependencies: "dependencies_benefits",
  Benefits: "dependencies_benefits",
  Decisions: "full_raid",
  Changes: "full_raid",
  Lessons: "full_raid",
};

const LOG_FEATURES = {
  decisions: "full_raid",
  "change-requests": "full_raid",
  lessons: "full_raid",
  benefits: "dependencies_benefits",
  milestones: null,
};

const duration = (start, end) => {
  if (!start || !end) return null;
  const d = Math.round((new Date(end) - new Date(start)) / 86400000);
  return Number.isNaN(d) ? null : d;
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-medium text-slate-800">{value || "—"}</span>
  </div>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useBranding();
  const { can, loading: plansLoading } = useEntitlements();
  const [tab, setTab] = useState("Overview");
  const [project, setProject] = useState(null);
  const [lists, setLists] = useState({});
  const [risks, setRisks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [assumptions, setAssumptions] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [logs, setLogs] = useState({ decisions: [], "change-requests": [], lessons: [], benefits: [], milestones: [] });
  const [periods, setPeriods] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [programmes, setProgrammes] = useState([]);

  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const [draft, setDraft] = useState({});

  const loadRaid = useCallback(async () => {
    const [r, i, a] = await Promise.all([
      api.get(`/projects/${id}/risks`),
      api.get(`/projects/${id}/issues`),
      can("full_raid") ? api.get(`/projects/${id}/assumptions`) : Promise.resolve({ data: [] }),
    ]);
    setRisks(r.data);
    setIssues(i.data);
    setAssumptions(a.data);
  }, [id, can]);

  const loadLog = useCallback(
    async (key) => {
      const { data } = await api.get(`/projects/${id}/${key}`);
      setLogs((l) => ({ ...l, [key]: data }));
    },
    [id],
  );

  const loadProject = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}`);
    setProject(data);
    setDraft(data);
  }, [id]);

  useEffect(() => {
    // Wait for the plan before requesting gated registers, otherwise the optimistic default
    // fires requests the plan does not allow.
    if (plansLoading) return;
    loadProject();
    loadRaid();
    api.get(`/projects/${id}/status-updates`).then(({ data }) => setUpdates(data));
    api.get("/config/lists").then(({ data }) => setLists(Object.fromEntries(data.map((l) => [l.key, l.values]))));
    if (can("reporting_periods")) api.get("/reporting-periods").then(({ data }) => setPeriods(data));
    api.get("/portfolios").then(({ data }) => setPortfolios(data));
    api.get("/programmes").then(({ data }) => setProgrammes(data));
    // Only fetch registers this plan includes — otherwise every load fires refused requests.
    ["decisions", "change-requests", "lessons", "benefits", "milestones"]
      .filter((k) => can(LOG_FEATURES[k]))
      .forEach((k) => loadLog(k));
  }, [id, loadProject, loadRaid, loadLog, can, plansLoading]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));  const setD = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  const downloadStatusPdf = async (u) => {
    try {
      const res = await api.get(`/status-updates/${u.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `status-report-${u.period_ending || u.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Weekly report downloaded");
    } catch (e) {
      toast.error("Could not generate the report PDF");
    }
  };

  const openDialog = (kind) => {
    const defaults = {
      risk: { title: "", description: "", risk_type: "", probability: "Medium", impact: "Medium", mitigation: "", owner: "", due_date: "", status: "Open", is_now_an_issue: false },
      issue: { title: "", description: "", issue_type: "", severity: "Medium", resolution: "", owner: "", due_date: "", status: "Open" },
      assumption: { title: "", description: "", confidence: "Medium", owner: "", status: "Open" },
      status: {
        reporting_period_id: periods.find((p) => p.status === "Open")?.id || "",
        period_ending: periods.find((p) => p.status === "Open")?.period_ending || new Date().toISOString().slice(0, 10),
        exec_summary: "",
        progress_this_period: "",
        upcoming_focus: updates[0]?.upcoming_focus || "",
        overall_rag: project?.rag || "Green",
        plan_rag: "Green",
        resource_rag: "Green", finance_rag: "Green", benefits_rag: "Green", vendor_rag: "Green",
        percent_complete: project?.percent_complete || 0,
      },
    }[kind];
    setForm(defaults);
    setDialog(kind);
  };

  const submit = async () => {
    try {
      if (dialog === "risk") {
        if (!form.title?.trim()) return toast.error("Title required");
        await api.post(`/projects/${id}/risks`, form);
        toast.success(form.is_now_an_issue ? "Risk logged and converted to an Issue" : "Risk logged");
        await loadRaid();
      } else if (dialog === "issue") {
        if (!form.title?.trim()) return toast.error("Title required");
        await api.post(`/projects/${id}/issues`, form);
        toast.success("Issue logged");
        await loadRaid();
      } else if (dialog === "assumption") {
        if (!form.title?.trim()) return toast.error("Title required");
        await api.post(`/projects/${id}/assumptions`, form);
        toast.success("Assumption logged");
        await loadRaid();
      } else if (dialog === "status") {
        if (!form.exec_summary?.trim()) return toast.error("Exec summary required");
        await api.post(`/projects/${id}/status-updates`, { ...form, percent_complete: Number(form.percent_complete) || 0 });
        toast.success("Status update submitted — RAG rolled up to portfolio");
        const { data } = await api.get(`/projects/${id}/status-updates`);
        setUpdates(data);
        await loadProject();
      }
      setDialog(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const aiDraftStatus = async () => {
    setAiBusy("draft");
    try {
      const { data } = await api.post("/assistant/status-draft", { project_id: id });
      if (data.error) return toast.error(data.error);
      setForm((f) => ({
        ...f,
        exec_summary: data.exec_summary || f.exec_summary,
        progress_this_period: data.progress_this_period || f.progress_this_period,
        upcoming_focus: data.upcoming_focus || f.upcoming_focus,
        overall_rag: data.overall_rag || f.overall_rag,
        resource_rag: data.resource_rag || f.resource_rag,
        finance_rag: data.finance_rag || f.finance_rag,
        benefits_rag: data.benefits_rag || f.benefits_rag,
        vendor_rag: data.vendor_rag || f.vendor_rag,
      }));
      toast.success("Draft inserted — review before submitting");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAiBusy("");
    }
  };

  const aiClassify = async () => {
    if (!form.title?.trim()) return toast.error("Add a title first");
    setAiBusy("classify");
    try {
      const { data } = await api.post("/assistant/classify", {
        kind: dialog, title: form.title, description: form.description || null, project_id: id,
      });
      if (data.error) return toast.error(data.error);
      setForm((f) => ({
        ...f,
        ...(dialog === "risk"
          ? { risk_type: data.type || f.risk_type, probability: data.probability || f.probability, impact: data.impact || f.impact }
          : { issue_type: data.type || f.issue_type, severity: data.severity || f.severity }),
      }));
      toast.success(data.rationale || "Classification suggested");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAiBusy("");
    }
  };

  const aiMitigation = async (risk) => {
    setAiBusy(risk.id);
    try {
      const { data } = await api.post("/assistant/mitigation", { risk_id: risk.id });
      if (data.error) return toast.error(data.error);
      const text = [data.mitigation, ...(data.actions || []).map((a) => `• ${a}`)].filter(Boolean).join("\n");
      if (!window.confirm(`Suggested mitigation:\n\n${text}\n\nSave this to the risk?`)) return;
      await api.put(`/risks/${risk.id}`, { ...risk, mitigation: text, is_now_an_issue: false });
      await loadRaid();
      toast.success("Mitigation saved to the risk");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAiBusy("");
    }
  };

  const convertRisk = async (r) => {
    try {
      await api.put(`/risks/${r.id}`, { ...r, is_now_an_issue: true });
      toast.success("Issue created from risk");
      await loadRaid();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const requestApproval = async (approvalType, itemId, level = "Level 1") => {
    try {
      const { data } = await api.post("/approvals/request", {
        approval_type: approvalType,
        project_id: id,
        item_id: itemId,
        level,
      });
      toast.success(`Routed to ${data.routed_to} approver${data.routed_to === 1 ? "" : "s"}`);
      if (approvalType === "Decision") await loadLog("decisions");
      if (approvalType === "Change Request") await loadLog("change-requests");
      if (approvalType === "Stage Gate") await loadProject();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const del = async (path, reload) => {
    try {
      await api.delete(path);
      await reload();
      toast.success("Deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveProject = async () => {
    try {
      const { data } = await api.put(`/projects/${id}`, {
        ...draft,
        percent_complete: Number(draft.percent_complete) || 0,
        budget: draft.budget === "" || draft.budget === null || draft.budget === undefined ? null : Number(draft.budget),
      });
      setProject(data);
      setEditing(false);
      toast.success("Project updated");
      const fw = draft.governance_framework;
      if (fw && fw !== project.governance_framework) {
        try {
          const { data: applied } = await api.post(`/projects/${id}/apply-framework`, {
            framework: fw, include_standard_risks: true, set_stage: !draft.stage,
          });
          const { data: fresh } = await api.get(`/projects/${id}`);
          setProject(fresh);
          setDraft(fresh);
          await loadRaid();
          toast.success(`${fw} template applied · ${applied.risks_created} standard risks added`);
        } catch (e) {
          toast.message(apiError(e.response?.data?.detail));
        }
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!project) return <Shell testId="project-detail-loading"><Empty text="Loading project…" /></Shell>;

  return (
    <Shell
      testId="project-detail-page"
      title={project.name}
      subtitle={`${project.code || "No code"} · ${project.stage || "No stage"} · ${project.governance_framework || "No framework"}`}
      actions={
        <div className="flex items-center gap-2">
          <RagBadge value={project.rag} testId="detail-rag-badge" />
          {project.gate_status && <StatusPill value={project.gate_status} />}
          {can("approvals_stage_gates") && (
            <>
              <Button variant="outline" data-testid="request-gate-approval-btn" onClick={() => requestApproval("Stage Gate", project.id)} className="gap-2">
                <Stamp size={15} /> {t("Request Stage Gate Approval")}
              </Button>
              <Button variant="outline" data-testid="view-approvals-btn" onClick={() => navigate("/approvals")} className="gap-2">
                Approvals
              </Button>
            </>
          )}
          <Button variant="outline" data-testid="back-to-projects-btn" onClick={() => navigate("/projects")} className="gap-2">
            <ArrowLeft size={15} /> Back
          </Button>
        </div>
      }
    >
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.filter((tabName) => can(TAB_FEATURES[tabName])).map((tabName) => (
          <button
            key={tabName}
            data-testid={`detail-tab-${tabName.toLowerCase().replace(/ /g, "-")}`}
            onClick={() => setTab(tabName)}
            className={`relative shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium transition-colors ${tab === tabName ? "text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
          >
            {t(tabName)}
            {tab === tabName && <span className="absolute inset-x-2 -bottom-px h-0.5" style={{ backgroundColor: "var(--brand-primary)" }} />}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="panel p-5 lg:col-span-2" data-testid="overview-panel">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-base font-semibold text-slate-800">Project Record</h3>
              {editing ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="cancel-edit-btn" onClick={() => { setDraft(project); setEditing(false); }}>Cancel</Button>
                  <Button size="sm" data-testid="save-project-btn" onClick={saveProject} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Save size={14} /> Save</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" data-testid="edit-project-btn" onClick={() => setEditing(true)}>Edit</Button>
              )}
            </div>

            {editing ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><label className="field-label">Name</label><Input data-testid="edit-name-input" value={draft.name || ""} onChange={(e) => setD("name")(e.target.value)} /></div>
                <div><label className="field-label">Project Code</label><Input data-testid="edit-code-input" value={draft.code || ""} onChange={(e) => setD("code")(e.target.value)} /></div>
                <div><label className="field-label">Status</label><Select testId="edit-status-select" value={draft.status} onChange={setD("status")} options={["Active", "On Hold", "Closed", "Cancelled"]} placeholder="Select" /></div>

                <div className="sm:col-span-2 mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Ownership</div>
                <div><label className="field-label">Portfolio</label><Select testId="edit-portfolio-select" value={draft.portfolio_id} onChange={setD("portfolio_id")} options={portfolios.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select portfolio" /></div>
                <div><label className="field-label">Programme</label><Select testId="edit-programme-select" value={draft.programme_id} onChange={setD("programme_id")} options={programmes.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select programme" /></div>
                <div><label className="field-label">Project Manager</label><Input data-testid="edit-pm-input" value={draft.project_manager || ""} onChange={(e) => setD("project_manager")(e.target.value)} /></div>
                <div><label className="field-label">Project Owner</label><Input data-testid="edit-project-owner-input" value={draft.project_owner || ""} onChange={(e) => setD("project_owner")(e.target.value)} /></div>
                <div><label className="field-label">Exec Sponsor</label><Input data-testid="edit-sponsor-input" value={draft.sponsor || ""} onChange={(e) => setD("sponsor")(e.target.value)} /></div>
                <div><label className="field-label">Business Owner</label><Input data-testid="edit-business-owner-input" value={draft.business_owner || ""} onChange={(e) => setD("business_owner")(e.target.value)} /></div>
                <div><label className="field-label">PMO Coordinator</label><Input data-testid="edit-pmo-coordinator-input" value={draft.pmo_coordinator || ""} onChange={(e) => setD("pmo_coordinator")(e.target.value)} /></div>

                <div className="sm:col-span-2 mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Governance & Tracking</div>
                <div><label className="field-label">Governance Framework</label><Select testId="edit-governance-select" value={draft.governance_framework} onChange={setD("governance_framework")} options={lists.governance_framework || []} placeholder="Select" /></div>
                <div><label className="field-label">Stage / Gate</label><Select testId="edit-stage-select" value={draft.stage} onChange={setD("stage")} options={lists.stage || []} placeholder="Select" /></div>
                <div><label className="field-label">RAG</label><Select testId="edit-rag-select" value={draft.rag} onChange={setD("rag")} options={RAG_OPTIONS} placeholder="Select" /></div>
                <div><label className="field-label">Priority</label><Select testId="edit-priority-select" value={draft.priority} onChange={setD("priority")} options={lists.priority || []} placeholder="Select" /></div>
                <div><label className="field-label">Department</label><Select testId="edit-department-select" value={draft.department} onChange={setD("department")} options={lists.department || []} placeholder="Select" /></div>
                <div><label className="field-label">% Complete</label><Input data-testid="edit-percent-input" type="number" min="0" max="100" value={draft.percent_complete ?? 0} onChange={(e) => setD("percent_complete")(e.target.value)} /></div>
                <div><label className="field-label">Start Date</label><Input data-testid="edit-start-input" type="date" value={draft.start_date || ""} onChange={(e) => setD("start_date")(e.target.value)} /></div>
                <div><label className="field-label">End Date</label><Input data-testid="edit-end-input" type="date" value={draft.end_date || ""} onChange={(e) => setD("end_date")(e.target.value)} /></div>
                <div>
                  <label className="field-label">Duration (days)</label>
                  <div data-testid="edit-duration-derived" className="flex h-9 items-center text-sm text-slate-500">
                    {duration(draft.start_date, draft.end_date) ?? "Set both dates"}
                  </div>
                </div>
                <div><label className="field-label">Budget</label><Input data-testid="edit-budget-input" type="number" value={draft.budget ?? ""} onChange={(e) => setD("budget")(e.target.value)} /></div>

                <div className="sm:col-span-2 mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Narrative</div>
                <div className="sm:col-span-2"><label className="field-label">Description</label><Textarea data-testid="edit-description-input" value={draft.description || ""} onChange={(e) => setD("description")(e.target.value)} rows={3} /></div>
                <div className="sm:col-span-2"><label className="field-label">Background</label><Textarea data-testid="edit-background-input" value={draft.background || ""} onChange={(e) => setD("background")(e.target.value)} rows={3} /></div>
                <div className="sm:col-span-2"><label className="field-label">Objectives</label><Textarea data-testid="edit-objectives-input" value={draft.objectives || ""} onChange={(e) => setD("objectives")(e.target.value)} rows={3} /></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                <div>
                  <Row label="Portfolio" value={portfolios.find((x) => x.id === project.portfolio_id)?.name} />
                  <Row label="Programme" value={programmes.find((x) => x.id === project.programme_id)?.name} />
                  <Row label="Project Owner" value={project.project_owner} />
                  <Row label="Project Manager" value={project.project_manager} />
                  <Row label="Exec Sponsor" value={project.sponsor} />
                  <Row label="Business Owner" value={project.business_owner} />
                  <Row label="PMO Coordinator" value={project.pmo_coordinator} />
                  <Row label="Department" value={project.department} />
                  <Row label="Priority" value={project.priority} />
                </div>
                <div>
                  <Row label="Governance" value={project.governance_framework} />
                  <Row label="Stage / Gate" value={project.stage} />
                  <Row label="Start Date" value={project.start_date} />
                  <Row label="End Date" value={project.end_date} />
                  <Row label="Duration (days)" value={duration(project.start_date, project.end_date)} />
                  <Row label="Budget" value={project.budget ?? null} />
                  <Row label="Status" value={project.status} />
                </div>
                {project.background && (
                  <div className="mt-4 sm:col-span-2">
                    <div className="field-label">Background</div>
                    <p className="text-sm leading-relaxed text-slate-700">{project.background}</p>
                  </div>
                )}
                {project.objectives && (
                  <div className="mt-4 sm:col-span-2">
                    <div className="field-label">Objectives</div>
                    <p className="text-sm leading-relaxed text-slate-700">{project.objectives}</p>
                  </div>
                )}
                {project.description && (
                  <div className="mt-4 sm:col-span-2">
                    <div className="field-label">Description</div>
                    <p className="text-sm leading-relaxed text-slate-700">{project.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="panel p-5" data-testid="progress-panel">
              <div className="field-label">Progress</div>
              <div className="font-display text-4xl font-bold text-slate-900">{project.percent_complete || 0}%</div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${project.percent_complete || 0}%`, backgroundColor: "var(--brand-primary)" }} />
              </div>
            </div>
            <div className="panel p-5" data-testid="raid-summary-panel">
              <div className="field-label">Governance Summary</div>
              {[["Risks", risks.length], ["Issues", issues.length],
                ...(can("full_raid") ? [["Assumptions", assumptions.length], ["Decisions", logs.decisions.length], ["Change Requests", logs["change-requests"].length], ["Lessons", logs.lessons.length]] : []),
                ...(can("dependencies_benefits") ? [["Benefits", logs.benefits.length]] : [])].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-slate-100 py-2 text-sm last:border-0">
                  <span className="text-slate-500">{l}</span>
                  <span className="font-semibold text-slate-800">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "RAID" && (
        <div className="space-y-6">
          <section className="panel overflow-x-auto p-5" data-testid="risks-section">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-display text-base font-semibold text-slate-800">Risks</h3>
              <div className="flex items-center gap-2">
                <ImportControls projectId={id} kind="risks" onDone={loadRaid} />
                <Button size="sm" data-testid="add-risk-btn" onClick={() => openDialog("risk")} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Plus size={14} /> Log Risk</Button>
              </div>
            </div>
            {risks.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No risks logged.</p> : (
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>{["Title", "Type", "Prob", "Impact", "Owner", "Due", "Status", ""].map((h) => <th key={h} className="pb-2 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {risks.map((r) => (
                    <tr key={r.id} data-testid={`risk-row-${r.id}`}>
                      <td className="py-2.5 pr-3 font-medium text-slate-800">{r.title}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{r.risk_type || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{r.probability}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{r.impact}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{r.owner || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{r.due_date || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{r.status}</td>
                      <td className="py-2.5 text-right">
                        {can("ai_assistant") && (
                        <button data-testid={`ai-mitigation-${r.id}`} onClick={() => aiMitigation(r)} disabled={aiBusy === r.id} title="Suggest mitigation with the Insights Assistant" aria-label="Suggest mitigation" className="mr-2 text-slate-400 transition-colors hover:text-indigo-600 disabled:opacity-40">
                          {aiBusy === r.id ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                        </button>
                        )}
                        {r.status !== "Converted to Issue" && (
                          <button data-testid={`convert-risk-${r.id}`} onClick={() => convertRisk(r)} title="Is now an issue" className="mr-2 text-slate-400 transition-colors hover:text-amber-600">
                            <ArrowRightLeft size={15} />
                          </button>
                        )}
                        <button data-testid={`delete-risk-${r.id}`} onClick={() => window.confirm("Delete this risk?") && del(`/risks/${r.id}`, loadRaid)} aria-label="Delete risk" title="Delete risk" className="text-slate-400 transition-colors hover:text-red-600"><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="panel overflow-x-auto p-5" data-testid="issues-section">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-display text-base font-semibold text-slate-800">Issues</h3>
              <div className="flex items-center gap-2">
                <ImportControls projectId={id} kind="issues" onDone={loadRaid} />
                <Button size="sm" data-testid="add-issue-btn" onClick={() => openDialog("issue")} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Plus size={14} /> Log Issue</Button>
              </div>
            </div>
            {issues.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No issues logged.</p> : (
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>{["Title", "Type", "Severity", "Owner", "Due", "Status", "Source", ""].map((h) => <th key={h} className="pb-2 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {issues.map((i) => (
                    <tr key={i.id} data-testid={`issue-row-${i.id}`}>
                      <td className="py-2.5 pr-3 font-medium text-slate-800">{i.title}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{i.issue_type || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{i.severity}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{i.owner || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{i.due_date || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{i.status}</td>
                      <td className="py-2.5 pr-3 text-xs text-slate-400">{i.source_risk_id ? "From risk" : "Direct"}</td>
                      <td className="py-2.5 text-right"><button data-testid={`delete-issue-${i.id}`} onClick={() => window.confirm("Delete this issue?") && del(`/issues/${i.id}`, loadRaid)} aria-label="Delete issue" title="Delete issue" className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {can("full_raid") && (
          <section className="panel overflow-x-auto p-5" data-testid="assumptions-section">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-base font-semibold text-slate-800">Assumptions</h3>
              <Button size="sm" data-testid="add-assumption-btn" onClick={() => openDialog("assumption")} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Plus size={14} /> Log Assumption</Button>
            </div>
            {assumptions.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No assumptions logged.</p> : (
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>{["Title", "Confidence", "Owner", "Status", ""].map((h) => <th key={h} className="pb-2 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {assumptions.map((a) => (
                    <tr key={a.id} data-testid={`assumption-row-${a.id}`}>
                      <td className="py-2.5 pr-3 font-medium text-slate-800">{a.title}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{a.confidence}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{a.owner || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{a.status}</td>
                      <td className="py-2.5 text-right"><button data-testid={`delete-assumption-${a.id}`} onClick={() => window.confirm("Delete this assumption?") && del(`/assumptions/${a.id}`, loadRaid)} aria-label="Delete assumption" title="Delete assumption" className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          )}
        </div>
      )}

      {tab === "Status Updates" && (
        <div className="space-y-4" data-testid="status-updates-section">
          <div className="flex justify-end">
            <Button data-testid="add-status-btn" onClick={() => openDialog("status")} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Plus size={14} /> New Status Update</Button>
          </div>
          {updates.length === 0 ? <Empty testId="status-empty" text="No status updates yet. Submit the first weekly update." /> : (
            updates.map((u) => (
              <div key={u.id} className="panel p-5" data-testid={`status-row-${u.id}`}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-display text-sm font-semibold text-slate-800">{u.reporting_period_name || `Period ending ${u.period_ending || u.created_at?.slice(0, 10)}`}</div>
                    <div className="text-xs text-slate-400">by {u.created_by} · {u.percent_complete ?? 0}% complete{u.previous_overall_rag ? ` · previous overall ${u.previous_overall_rag}` : ""}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <RagBadge value={u.overall_rag} label={`Overall ${u.overall_rag}`} />
                    {u.plan_rag && <RagBadge value={u.plan_rag} label="Plan" />}
                    <RagBadge value={u.resource_rag} label="Resource" />
                    <RagBadge value={u.finance_rag} label="Finance" />
                    <RagBadge value={u.benefits_rag} label="Benefits" />
                    <RagBadge value={u.vendor_rag} label="Vendor" />
                    <Button size="sm" variant="outline" data-testid={`status-pdf-${u.id}`} onClick={() => downloadStatusPdf(u)} className="gap-1.5">
                      <FileDown size={14} /> PDF
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                  {[["Exec Summary", u.exec_summary], ["Progress This Period", u.progress_this_period], ["Upcoming Focus", u.upcoming_focus]].map(([l, v]) => (
                    <div key={l}>
                      <div className="field-label">{l}</div>
                      <p className="text-sm leading-relaxed text-slate-700">{v || "—"}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "Documents" && <Documents projectId={id} stages={lists.stage || []} />}

      {tab === "Plan" && <Schedule projectId={id} stages={lists.stage || []} />}

      {tab === "Milestones" && (
        <LogSection
          title="Milestones"
          slug="milestones"
          addLabel="Add Milestone"
          description="Baseline vs forecast dates. Milestones within 30 days either side of the reporting date appear on the weekly status report."
          items={logs.milestones}
          reload={() => loadLog("milestones")}
          createPath={`/projects/${id}/milestones`}
          deletePath="/milestones"
          columns={[
            { key: "ref", label: "ID" },
            { key: "title", label: "Milestone" },
            { key: "milestone_type", label: "Type" },
            { key: "baseline_date", label: "Baseline" },
            { key: "forecast_date", label: "Forecast" },
            { key: "actual_date", label: "Actual" },
            { key: "rag", label: "RAG", render: (m) => <RagBadge value={m.rag} /> },
            { key: "status", label: "Status", render: (m) => <StatusPill value={m.status} /> },
            { key: "include_in_status_report", label: "On Report", render: (m) => (m.include_in_status_report === false ? "No" : "Yes") },
          ]}
          fields={[
            { key: "title", label: "Milestone", required: true, span: 2 },
            { key: "milestone_type", label: "Type", type: "select", options: ["Major", "Minor", "Stage Gate", "Key Deliverable"], default: "Major" },
            { key: "stage", label: "Project Stage", type: "select", options: lists.stage || [] },
            { key: "owner", label: "Owner" },
            { key: "status", label: "Status", type: "select", options: ["Not Started", "In Progress", "Complete", "Delayed"], default: "Not Started" },
            { key: "baseline_date", label: "Baseline Date", type: "date" },
            { key: "forecast_date", label: "Forecast Date", type: "date" },
            { key: "actual_date", label: "Actual Date", type: "date" },
            { key: "description", label: "Description", type: "textarea", span: 2 },
          ]}
        />
      )}

      {tab === "Dependencies" && <Dependencies projectId={id} />}

      {tab === "Decisions" && (
        <LogSection
          title="Decisions"
          slug="decisions"
          description="Key decisions with criticality and approver sign-off."
          items={logs.decisions}
          reload={() => loadLog("decisions")}
          createPath={`/projects/${id}/decisions`}
          deletePath="/decisions"
          renderActions={(d) => d.approval_status !== "Approved" && (
            <button data-testid={`request-approval-decision-${d.id}`} onClick={() => requestApproval("Decision", d.id)} title="Request approval" aria-label="Request approval" className="text-slate-400 hover:text-slate-800">
              <Stamp size={15} />
            </button>
          )}
          columns={[
            { key: "title", label: "Decision" },
            { key: "criticality", label: "Criticality", render: (d) => <StatusPill value={d.criticality} /> },
            { key: "owner", label: "Owner" },
            { key: "approver", label: "Approver" },
            { key: "approval_status", label: "Status", render: (d) => <StatusPill value={d.approval_status} /> },
            { key: "decision_date", label: "Decided" },
          ]}
          fields={[
            { key: "title", label: "Decision", required: true, span: 2 },
            { key: "criticality", label: "Criticality", type: "select", options: ["Low", "Medium", "High", "Critical"], default: "Medium" },
            { key: "owner", label: "Owner" },
            { key: "approver", label: "Approver" },
            { key: "approval_status", label: "Approval Status", type: "select", options: ["Pending", "Approved", "Rejected"], default: "Pending" },
            { key: "decision_date", label: "Decision Date", type: "date" },
            { key: "description", label: "Description", type: "textarea", span: 2 },
            { key: "rationale", label: "Rationale", type: "textarea", span: 2 },
          ]}
        />
      )}

      {tab === "Changes" && (
        <LogSection
          title="Change Requests"
          slug="changes"
          addLabel="Raise Change Request"
          description="Scope changes with cost impact, reason for change and approval level."
          items={logs["change-requests"]}
          reload={() => loadLog("change-requests")}
          createPath={`/projects/${id}/change-requests`}
          deletePath="/change-requests"
          renderActions={(c) => c.approval_status !== "Approved" && (
            <button data-testid={`request-approval-change-${c.id}`} onClick={() => requestApproval("Change Request", c.id, c.approval_level || "Level 1")} title="Request approval" aria-label="Request approval" className="text-slate-400 hover:text-slate-800">
              <Stamp size={15} />
            </button>
          )}
          columns={[
            { key: "title", label: "Change" },
            { key: "reason_for_change", label: "Reason" },
            { key: "cost_impact", label: "Cost Impact", render: (c) => (c.cost_impact != null ? c.cost_impact.toLocaleString() : "—") },
            { key: "schedule_impact_days", label: "Days" },
            { key: "approval_level", label: "Level" },
            { key: "approver", label: "Approver" },
            { key: "approval_status", label: "Status", render: (c) => <StatusPill value={c.approval_status} /> },
          ]}
          fields={[
            { key: "title", label: "Change Title", required: true, span: 2 },
            { key: "reason_for_change", label: "Reason for Change", span: 2 },
            { key: "cost_impact", label: "Cost Impact", type: "number", placeholder: "e.g. 25000" },
            { key: "schedule_impact_days", label: "Schedule Impact (days)", type: "number" },
            { key: "approval_level", label: "Approval Level", type: "select", options: ["Level 1", "Level 2", "Level 3"], default: "Level 1" },
            { key: "approver", label: "Approver" },
            { key: "approval_status", label: "Approval Status", type: "select", options: ["Pending", "Approved", "Rejected"], default: "Pending" },
            { key: "requested_by", label: "Requested By" },
            { key: "decision_date", label: "Decision Date", type: "date" },
            { key: "description", label: "Description", type: "textarea", span: 2 },
          ]}
        />
      )}

      {tab === "Lessons" && (
        <LogSection
          title="Lessons Learned"
          slug="lessons"
          addLabel="Log Lesson"
          description="Captured by project stage and topic, flagged as a success or improvable."
          items={logs.lessons}
          reload={() => loadLog("lessons")}
          createPath={`/projects/${id}/lessons`}
          deletePath="/lessons"
          columns={[
            { key: "title", label: "Lesson" },
            { key: "stage", label: "Stage" },
            { key: "topic", label: "Topic" },
            { key: "category", label: "Category", render: (l) => <StatusPill value={l.category} /> },
            { key: "owner", label: "Owner" },
          ]}
          fields={[
            { key: "title", label: "Lesson", required: true, span: 2 },
            { key: "stage", label: "Project Stage", type: "select", options: lists.stage || [] },
            { key: "topic", label: "Topic", placeholder: "e.g. Vendor management" },
            { key: "category", label: "Category", type: "select", options: ["Success", "Improvable"], default: "Success" },
            { key: "owner", label: "Owner" },
            { key: "description", label: "What happened", type: "textarea", span: 2 },
            { key: "recommendation", label: "Recommendation", type: "textarea", span: 2 },
          ]}
        />
      )}

      {tab === "Benefits" && (
        <LogSection
          title="Benefits"
          slug="benefits"
          addLabel="Log Benefit"
          description="Benefits realization tracking by category, rating and closure."
          items={logs.benefits}
          reload={() => loadLog("benefits")}
          createPath={`/projects/${id}/benefits`}
          deletePath="/benefits"
          columns={[
            { key: "title", label: "Benefit" },
            { key: "category", label: "Category" },
            { key: "rating", label: "Rating", render: (b) => <StatusPill value={b.rating} /> },
            { key: "target_value", label: "Target" },
            { key: "owner", label: "Owner" },
            { key: "status", label: "Status", render: (b) => <StatusPill value={b.status} /> },
            { key: "closed_date", label: "Closed" },
          ]}
          fields={[
            { key: "title", label: "Benefit", required: true, span: 2 },
            { key: "category", label: "Category", type: "select", options: ["Financial", "Efficiency", "Customer", "Compliance", "Strategic"], default: "Financial" },
            { key: "rating", label: "Rating", type: "select", options: ["Low", "Medium", "High"], default: "Medium" },
            { key: "target_value", label: "Target Value", placeholder: "e.g. 400k p.a." },
            { key: "owner", label: "Owner" },
            { key: "status", label: "Status", type: "select", options: ["Planned", "In Progress", "Realized", "Not Realized"], default: "Planned" },
            { key: "closed_date", label: "Closed Date", type: "date" },
            { key: "description", label: "Description", type: "textarea", span: 2 },
          ]}
        />
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="raid-dialog" className="max-h-[88vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="capitalize">{dialog === "status" ? "New Status Update" : `Log ${dialog}`}</DialogTitle>
            <DialogDescription>
              {dialog === "status"
                ? "Submit the weekly update. RAG values roll up to the project and portfolio dashboard."
                : `Add a new ${dialog} entry to this project's RAID log.`}
            </DialogDescription>
          </DialogHeader>

          {dialog === "risk" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className="field-label">Title *</label><Input data-testid="risk-title-input" value={form.title || ""} onChange={(e) => set("title")(e.target.value)} /></div>
              <div><label className="field-label">Risk Type</label><Select testId="risk-type-select" value={form.risk_type} onChange={set("risk_type")} options={lists.risk_type || []} placeholder="Select" /></div>
              <div><label className="field-label">Owner</label><Input data-testid="risk-owner-input" value={form.owner || ""} onChange={(e) => set("owner")(e.target.value)} /></div>
              <div><label className="field-label">Probability</label><Select testId="risk-probability-select" value={form.probability} onChange={set("probability")} options={["Low", "Medium", "High"]} placeholder="Select" /></div>
              <div><label className="field-label">Impact</label><Select testId="risk-impact-select" value={form.impact} onChange={set("impact")} options={["Low", "Medium", "High"]} placeholder="Select" /></div>
              <div><label className="field-label">Due Date</label><Input data-testid="risk-due-input" type="date" value={form.due_date || ""} onChange={(e) => set("due_date")(e.target.value)} /></div>
              <div><label className="field-label">Status</label><Select testId="risk-status-select" value={form.status} onChange={set("status")} options={["Open", "Mitigated", "Closed"]} placeholder="Select" /></div>
              <div className="sm:col-span-2"><label className="field-label">Description</label><Textarea data-testid="risk-description-input" rows={2} value={form.description || ""} onChange={(e) => set("description")(e.target.value)} /></div>
              <div className="sm:col-span-2"><label className="field-label">Mitigation</label><Textarea data-testid="risk-mitigation-input" rows={2} value={form.mitigation || ""} onChange={(e) => set("mitigation")(e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                <input data-testid="risk-is-issue-checkbox" type="checkbox" checked={!!form.is_now_an_issue} onChange={(e) => set("is_now_an_issue")(e.target.checked)} className="h-4 w-4" />
                Is Now an Issue (auto-creates a linked Issue)
              </label>
            </div>
          )}

          {dialog === "issue" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className="field-label">Title *</label><Input data-testid="issue-title-input" value={form.title || ""} onChange={(e) => set("title")(e.target.value)} /></div>
              <div><label className="field-label">Issue Type</label><Select testId="issue-type-select" value={form.issue_type} onChange={set("issue_type")} options={lists.issue_type || []} placeholder="Select" /></div>
              <div><label className="field-label">Severity</label><Select testId="issue-severity-select" value={form.severity} onChange={set("severity")} options={["Low", "Medium", "High"]} placeholder="Select" /></div>
              <div><label className="field-label">Owner</label><Input data-testid="issue-owner-input" value={form.owner || ""} onChange={(e) => set("owner")(e.target.value)} /></div>
              <div><label className="field-label">Due Date</label><Input data-testid="issue-due-input" type="date" value={form.due_date || ""} onChange={(e) => set("due_date")(e.target.value)} /></div>
              <div className="sm:col-span-2"><label className="field-label">Description</label><Textarea data-testid="issue-description-input" rows={2} value={form.description || ""} onChange={(e) => set("description")(e.target.value)} /></div>
              <div className="sm:col-span-2"><label className="field-label">Resolution Plan</label><Textarea data-testid="issue-resolution-input" rows={2} value={form.resolution || ""} onChange={(e) => set("resolution")(e.target.value)} /></div>
            </div>
          )}

          {dialog === "assumption" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className="field-label">Title *</label><Input data-testid="assumption-title-input" value={form.title || ""} onChange={(e) => set("title")(e.target.value)} /></div>
              <div><label className="field-label">Confidence</label><Select testId="assumption-confidence-select" value={form.confidence} onChange={set("confidence")} options={["Low", "Medium", "High"]} placeholder="Select" /></div>
              <div><label className="field-label">Owner</label><Input data-testid="assumption-owner-input" value={form.owner || ""} onChange={(e) => set("owner")(e.target.value)} /></div>
              <div className="sm:col-span-2"><label className="field-label">Description</label><Textarea data-testid="assumption-description-input" rows={2} value={form.description || ""} onChange={(e) => set("description")(e.target.value)} /></div>
            </div>
          )}

          {dialog === "status" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="field-label">Reporting Period {periods.length === 0 && <span className="normal-case text-amber-600">(none set up — ask your PMO)</span>}</label>
                <Select
                  testId="status-period-select"
                  value={form.reporting_period_id}
                  onChange={(v) => setForm((f) => ({ ...f, reporting_period_id: v, period_ending: periods.find((p) => p.id === v)?.period_ending || f.period_ending }))}
                  options={periods.filter((p) => p.status === "Open").map((p) => ({ value: p.id, label: p.name }))}
                  placeholder="Select reporting period"
                />
              </div>
              <div><label className="field-label">Period Ending</label><Input data-testid="status-period-input" type="date" value={form.period_ending || ""} onChange={(e) => set("period_ending")(e.target.value)} /></div>
              <div><label className="field-label">% Complete</label><Input data-testid="status-percent-input" type="number" min="0" max="100" value={form.percent_complete ?? 0} onChange={(e) => set("percent_complete")(e.target.value)} /></div>
              <div>
                <label className="field-label">Previous Overall RAG</label>
                <div data-testid="status-previous-rag" className="flex h-9 items-center">
                  {updates[0]?.overall_rag ? <RagBadge value={updates[0].overall_rag} label={`Last week: ${updates[0].overall_rag}`} /> : <span className="text-sm text-slate-400">No previous update</span>}
                </div>
              </div>
              <div className="sm:col-span-2"><label className="field-label">Exec Summary *</label><Textarea data-testid="status-summary-input" rows={3} value={form.exec_summary || ""} onChange={(e) => set("exec_summary")(e.target.value)} /></div>
              <div className="sm:col-span-2"><label className="field-label">Progress This Period</label><Textarea data-testid="status-progress-input" rows={2} value={form.progress_this_period || ""} onChange={(e) => set("progress_this_period")(e.target.value)} /></div>
              <div className="sm:col-span-2">
                <label className="field-label">Upcoming Focus {updates[0] && <span className="normal-case text-slate-400">(pre-filled from last update)</span>}</label>
                <Textarea data-testid="status-focus-input" rows={2} value={form.upcoming_focus || ""} onChange={(e) => set("upcoming_focus")(e.target.value)} />
              </div>
              {[["overall_rag", "Overall RAG"], ["plan_rag", "Plan"], ["resource_rag", "Resource"], ["finance_rag", "Finance"], ["benefits_rag", "Benefits"], ["vendor_rag", "Vendor"]].map(([k, l]) => (
                <div key={k}><label className="field-label">{l}</label><Select testId={`status-${k}-select`} value={form[k]} onChange={set(k)} options={RAG_OPTIONS} placeholder="Select" /></div>
              ))}
            </div>
          )}

          <DialogFooter className="sm:justify-between">
            {dialog === "status" && can("ai_assistant") ? (
              <Button variant="outline" data-testid="ai-draft-status-btn" disabled={aiBusy === "draft"} onClick={aiDraftStatus} className="gap-1.5">
                {aiBusy === "draft" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Draft with Insights Assistant
              </Button>
            ) : (dialog === "risk" || dialog === "issue") && can("ai_assistant") ? (
              <Button variant="outline" data-testid="ai-classify-btn" disabled={aiBusy === "classify"} onClick={aiClassify} className="gap-1.5">
                {aiBusy === "classify" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Suggest Classification
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" data-testid="raid-cancel-btn" onClick={() => setDialog(null)}>Cancel</Button>
              <Button data-testid="raid-save-btn" onClick={submit} style={{ backgroundColor: "var(--brand-primary)" }}>Save</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
